// Fase I — Sessão 5A: Dashboard de telemetria do agente Nexo.
//
// Renderiza dados agregados de db.agent_telemetry com KPIs, timeseries
// (cost + cache hit), distribuições (tools + rounds) e split v1/v2.
//
// Acesso: só user em ADMIN_USER_IDS (gate AdminRoute em App.js, e
// segurança real é o 403 do backend em require_admin).

import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, RefreshCw, ArrowLeft, AlertCircle, Pause, Play,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useAuth } from "@/hooks/useAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const RANGES = [
  { id: "1h", label: "Última hora" },
  { id: "24h", label: "24 horas" },
  { id: "7d", label: "7 dias" },
  { id: "30d", label: "30 dias" },
];

const PIPELINE_VERSIONS = [
  { id: "all", label: "Todas" },
  { id: "v2", label: "v2 (agent)" },
  { id: "v1", label: "v1 (legacy)" },
];

// ── Formatters BR ────────────────────────────────────────────────
const fmtBRL = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 5, maximumFractionDigits: 5,
  })}`;
const fmtBRLShort = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
const fmtMs = (v) => `${Number(v || 0).toLocaleString("pt-BR")} ms`;
const fmtPct = (v) =>
  `${(Number(v || 0) * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  })}%`;
const fmtInt = (v) => Number(v || 0).toLocaleString("pt-BR");

const fmtTickShort = (iso, range) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (range === "1h" || range === "24h") {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

// ── Card primitivo ───────────────────────────────────────────────
function KpiCard({ label, value, sub, accent = "cyan" }) {
  const accentClass = {
    cyan: "text-cyan-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  }[accent] || "text-cyan-400";
  return (
    <div className="glass-card glow-hover rounded-xl p-4">
      <p className="text-[11px] tracking-[0.1em] uppercase text-slate-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 tabular-nums ${accentClass}`}>{value}</p>
      {sub ? <p className="text-[11px] text-slate-500 mt-1">{sub}</p> : null}
    </div>
  );
}

function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#0f1629] border border-white/10 rounded-lg p-2 shadow-xl">
      <p className="text-[11px] text-slate-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color || "#06b6d4" }}>
          {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="glass-card rounded-xl p-4 animate-pulse">
      <div className="h-3 w-20 bg-white/5 rounded mb-3" />
      <div className="h-8 w-32 bg-white/10 rounded" />
    </div>
  );
}
function SkeletonChart() {
  return (
    <div className="glass-card rounded-xl p-4 animate-pulse h-64">
      <div className="h-4 w-32 bg-white/5 rounded mb-4" />
      <div className="h-44 w-full bg-white/5 rounded" />
    </div>
  );
}

// ── Página principal ────────────────────────────────────────────
export default function AdminAgentPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [range, setRange] = useState("24h");
  const [pipelineVer, setPipelineVer] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  // Auto-refresh opcional. Default OFF; quando ON, refresh a cada 60s
  // E pausa automaticamente quando a aba fica oculta (visibilitychange API)
  // pra não disparar fetch infinito em background — alvo do brief #4.
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/api/admin/agent-telemetry?range=${range}&pipeline_version=${pipelineVer}`;
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
      const body = await r.json();
      setData(body);
      setLastUpdate(new Date());
    } catch (e) {
      setError(String(e.message || e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, pipelineVer, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh com pause em background tab (visibilitychange).
  useEffect(() => {
    if (!autoRefresh) return undefined;
    let timer = null;
    const tick = () => {
      if (document.visibilityState === "visible") {
        fetchData();
      }
    };
    timer = setInterval(tick, 60_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Quando volta a ficar visível, refresh imediato pra não exibir dado velho
        fetchData();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [autoRefresh, fetchData]);

  const summary = data?.summary;
  const isEmpty = summary && summary.total_turns === 0;

  // Timeseries com formatador de tick
  const tsBuckets = useMemo(
    () => (data?.timeseries?.buckets || []).map((b) => ({
      ...b,
      tickLabel: fmtTickShort(b.ts, range),
    })),
    [data, range],
  );

  const toolsList = useMemo(
    () => (data?.tools_distribution || []).slice(0, 12),
    [data],
  );

  const roundsList = useMemo(() => {
    const dist = data?.rounds_distribution || {};
    return Object.entries(dist)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([rounds, count]) => ({ rounds: `${rounds}r`, count }));
  }, [data]);

  return (
    <div className="min-h-screen" style={{ background: "#0B0F1A" }}>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-slate-200 transition"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-100 flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                Telemetria do Agente
              </h1>
              <p className="text-xs text-slate-500">
                Métricas agregadas de db.agent_telemetry · sessão 5A
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium transition ${
                autoRefresh
                  ? "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300"
                  : "bg-white/5 hover:bg-white/10 text-slate-400"
              }`}
              title={autoRefresh ? "Auto-refresh 60s ativo" : "Refresh manual"}
              data-testid="autorefresh-btn"
            >
              {autoRefresh ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              {autoRefresh ? "Auto 60s" : "Auto"}
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-medium transition disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-1 bg-[#0d1220] border border-white/[0.06] rounded-lg p-1">
            {RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 rounded-md text-xs transition ${
                  range === r.id
                    ? "bg-cyan-500/20 text-cyan-300"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-[#0d1220] border border-white/[0.06] rounded-lg p-1">
            {PIPELINE_VERSIONS.map((v) => (
              <button
                key={v.id}
                onClick={() => setPipelineVer(v.id)}
                className={`px-3 py-1.5 rounded-md text-xs transition ${
                  pipelineVer === v.id
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {lastUpdate && (
            <span className="text-[11px] text-slate-500 ml-auto self-center">
              atualizado {lastUpdate.toLocaleTimeString("pt-BR", {
                hour: "2-digit", minute: "2-digit", second: "2-digit",
              })}
            </span>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-300 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Falha ao carregar telemetria</p>
              <p className="text-xs text-rose-400/80 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && isEmpty && (
          <div className="glass-card rounded-xl p-8 text-center">
            <Activity className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">Sem turns nesse período</p>
            <p className="text-slate-500 text-xs mt-1">
              Nenhum turn do agente foi registrado em {RANGES.find(r => r.id === range)?.label.toLowerCase()}.
            </p>
          </div>
        )}

        {/* KPIs */}
        {loading && !data ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
          </div>
        ) : summary && !isEmpty ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard
              label="Custo total"
              value={fmtBRLShort(summary.total_cost_brl)}
              sub={`${fmtInt(summary.total_turns)} turns / ${fmtInt(summary.total_users)} users`}
              accent="cyan"
            />
            <KpiCard
              label="Custo médio / turn"
              value={fmtBRL(summary.avg_cost_per_turn)}
              sub={`p95 latência ${fmtMs(summary.p95_latency_ms)}`}
              accent="emerald"
            />
            <KpiCard
              label="Cache hit médio"
              value={fmtPct(summary.avg_cache_hit_rate)}
              sub={`p50 latência ${fmtMs(summary.p50_latency_ms)}`}
              accent="amber"
            />
            <KpiCard
              label="Error rate"
              value={fmtPct(summary.error_rate)}
              sub={`p99 latência ${fmtMs(summary.p99_latency_ms)}`}
              accent={summary.error_rate > 0.01 ? "rose" : "emerald"}
            />
          </div>
        ) : null}

        {/* Charts row 1: timeseries cost + cache hit */}
        {loading && !data ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
            <SkeletonChart /><SkeletonChart />
          </div>
        ) : data && !isEmpty ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
            <div className="glass-card glow-hover rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wide">
                Custo ao longo do tempo
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={tsBuckets} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-cost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2A44" />
                  <XAxis dataKey="tickLabel" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false}
                    tickFormatter={(v) => `R$ ${Number(v).toFixed(2)}`} />
                  <Tooltip content={<ChartTooltip formatter={fmtBRL} />} />
                  <Area type="monotone" dataKey="cost_brl" name="Custo"
                    stroke="#06b6d4" strokeWidth={2} fill="url(#grad-cost)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="glass-card glow-hover rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wide">
                Cache hit rate
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={tsBuckets} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-hit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2A44" />
                  <XAxis dataKey="tickLabel" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false}
                    domain={[0, 1]} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                  <Tooltip content={<ChartTooltip formatter={fmtPct} />} />
                  <Area type="monotone" dataKey="cache_hit_rate" name="Hit rate"
                    stroke="#22c55e" strokeWidth={2} fill="url(#grad-hit)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}

        {/* Charts row 2: tools + rounds */}
        {data && !isEmpty ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
            <div className="glass-card glow-hover rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wide">
                Distribuição de tools (top 12)
              </p>
              {toolsList.length === 0 ? (
                <p className="text-[11px] text-slate-500 text-center py-8">Nenhuma tool chamada</p>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, toolsList.length * 26)}>
                  <BarChart data={toolsList} layout="vertical"
                    margin={{ top: 5, right: 20, left: 60, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2A44" horizontal={false} />
                    <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="tool" stroke="#94a3b8" fontSize={10}
                      tickLine={false} axisLine={false} width={120} />
                    <Tooltip content={<ChartTooltip formatter={fmtInt} />} />
                    <Bar dataKey="count" name="Chamadas" fill="#a855f7" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="glass-card glow-hover rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wide">
                Rounds por turn
              </p>
              {roundsList.length === 0 ? (
                <p className="text-[11px] text-slate-500 text-center py-8">Sem dados</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={roundsList} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2A44" />
                    <XAxis dataKey="rounds" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                    <Tooltip content={<ChartTooltip formatter={fmtInt} />} />
                    <Bar dataKey="count" name="Turns" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        ) : null}

        {/* Pipeline split */}
        {data && !isEmpty && data.pipeline_split && Object.keys(data.pipeline_split).length > 0 ? (
          <div className="glass-card glow-hover rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wide">
              Split por pipeline
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="text-left py-2 px-3">Pipeline</th>
                    <th className="text-right py-2 px-3">Turns</th>
                    <th className="text-right py-2 px-3">Custo</th>
                    <th className="text-right py-2 px-3">Latência avg</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {Object.entries(data.pipeline_split).map(([ver, m]) => (
                    <tr key={ver}>
                      <td className="py-2 px-3 text-slate-200 font-medium">{ver}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-300">
                        {fmtInt(m.turns)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-cyan-400">
                        {fmtBRLShort(m.cost_brl)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-300">
                        {fmtMs(m.avg_latency_ms)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
